import { useTriggerLoad } from "@redotech/react-util/load";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoDropdownInputSize } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useLayoutEffect, useMemo, useState } from "react";

/**
 * Multi-select for collections, keyed and stored by collection name. The caller
 * provides `searchCollections` so this component stays platform-agnostic (and in
 * the shared layer) — each surface supplies its own Shopify/CommentSold/etc.
 * fetcher.
 */
export const CollectionNamesMultiSelect = memo(
  function CollectionNamesMultiSelect({
    value,
    setValue,
    searchCollections,
    label,
    description,
    placeholder,
    searchPlaceholder,
    disabled,
    size = "xs",
  }: {
    value: readonly string[];
    setValue: (names: string[]) => void;
    searchCollections: (args: {
      searchString: string;
      signal: AbortSignal;
    }) => Promise<string[]>;
    label?: string;
    description?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    size?: RedoDropdownInputSize;
  }) {
    const [searchString, setSearchString] = useState("");
    const [loading, setLoading] = useState(false);
    const [collectionsLoad, doCollectionsLoad] = useTriggerLoad(
      async (signal) => {
        try {
          return await searchCollections({ searchString, signal });
        } finally {
          setLoading(false);
        }
      },
    );

    useLayoutEffect(() => {
      doCollectionsLoad();
    }, [searchString, doCollectionsLoad]);

    const options = useMemo<BadgeRedoListItem<string>[]>(() => {
      const names = Array.from(
        new Set([...(collectionsLoad.value ?? []), ...value]),
      );
      return names.map((name) => ({
        id: name,
        value: name,
        type: "badge" as const,
        badge: { text: name, color: "gray" as const },
      }));
    }, [collectionsLoad.value, value]);

    const selectedOptions = useMemo<BadgeRedoListItem<string>[]>(
      () => options.filter((option) => value.includes(option.value)),
      [options, value],
    );

    return (
      <RedoTagsDropdownInput
        description={description}
        disabled={disabled}
        label={label}
        loading={loading}
        options={options}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        searchString={searchString}
        selectedOptions={selectedOptions}
        setSearchString={(newSearchString) => {
          setSearchString(newSearchString);
          setLoading(true);
        }}
        setSelectedOptions={(items) =>
          setValue(items.map((item) => item.value))
        }
        size={size}
      />
    );
  },
);
